import React, { useState, useEffect, useCallback } from 'react';
import './KnowledgeBase.css';
import { useAuth } from '../hooks/useAuth';

const CATEGORIES = [
  { value: 'billing', label: 'Billing' },
  { value: 'sync', label: 'Sync' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  title: '',
  category: 'billing',
  tags: '',
  content: '',
  is_published: true,
};

const KnowledgeBase = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [editing, setEditing] = useState(null); // null | 'new' | <article>
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (categoryFilter) params.set('category', categoryFilter);
      params.set('limit', '200');
      const res = await fetch(`/api/kb?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setArticles(data.articles || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const startNew = () => {
    setForm(EMPTY_FORM);
    setSaveError(null);
    setEditing('new');
  };

  const startEdit = (article) => {
    setForm({
      title: article.title || '',
      category: article.category || 'billing',
      tags: (article.tags || []).join(', '),
      content: article.content || '',
      is_published: article.is_published !== false,
    });
    setSaveError(null);
    setEditing(article);
  };

  const cancelEdit = () => {
    if (saving) return;
    setEditing(null);
    setSaveError(null);
  };

  const saveArticle = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        content: form.content,
        is_published: !!form.is_published,
        updated_by: user?.email || null,
      };
      if (!payload.title) throw new Error('Title is required');
      if (!payload.content.trim()) throw new Error('Content is required');

      const isNew = editing === 'new';
      const url = isNew ? '/api/kb' : `/api/kb/${editing._id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setEditing(null);
      fetchArticles();
    } catch (err) {
      setSaveError(err.message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/kb/${article._id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      fetchArticles();
    } catch (err) {
      alert(`Failed to delete: ${err.message}`);
    }
  };

  const categoryLabel = (value) => CATEGORIES.find(c => c.value === value)?.label || value;

  if (editing) {
    const isNew = editing === 'new';
    return (
      <div className="kb-container">
        <div className="kb-header">
          <button onClick={cancelEdit} className="kb-back-btn">← Back to Knowledge Base</button>
          <h2>{isNew ? 'New Article' : 'Edit Article'}</h2>
        </div>

        <div className="kb-form">
          <div className="kb-form-row">
            <label htmlFor="kb-title">Title</label>
            <input
              id="kb-title"
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. How to update your billing information"
              disabled={saving}
            />
          </div>

          <div className="kb-form-row-inline">
            <div className="kb-form-row">
              <label htmlFor="kb-category">Category</label>
              <select
                id="kb-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                disabled={saving}
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="kb-form-row">
              <label htmlFor="kb-tags">Tags (comma-separated)</label>
              <input
                id="kb-tags"
                type="text"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="e.g. invoice, stripe, payment"
                disabled={saving}
              />
            </div>
          </div>

          <div className="kb-form-row">
            <label htmlFor="kb-content">Content (plain text — for the agent's use)</label>
            <textarea
              id="kb-content"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Write the answer the agent should use to help users…"
              disabled={saving}
              rows={18}
            />
          </div>

          <div className="kb-form-row kb-checkbox-row">
            <label htmlFor="kb-published">
              <input
                id="kb-published"
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                disabled={saving}
              />
              Published (available to the agent)
            </label>
          </div>

          {saveError && <div className="kb-error">{saveError}</div>}

          <div className="kb-form-actions">
            <button onClick={cancelEdit} disabled={saving} className="kb-btn-secondary">
              Cancel
            </button>
            <button onClick={saveArticle} disabled={saving} className="kb-btn-primary">
              {saving ? 'Saving…' : (isNew ? 'Create Article' : 'Save Changes')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="kb-container">
      <div className="kb-header">
        <h2>Knowledge Base</h2>
        <p className="kb-subtitle">
          Articles the user-facing agent uses to answer billing, sync, and account questions.
        </p>
      </div>

      <div className="kb-toolbar">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, content, or tag…"
          className="kb-search"
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="kb-category-filter"
        >
          <option value="">All categories</option>
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <button onClick={startNew} className="kb-btn-primary">+ New Article</button>
      </div>

      {loading && <div className="kb-loading">Loading…</div>}
      {error && <div className="kb-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="kb-count">{total} article{total === 1 ? '' : 's'}</div>
          {articles.length === 0 ? (
            <div className="kb-empty">
              <p>No articles yet.</p>
              <button onClick={startNew} className="kb-btn-primary">Create the first one</button>
            </div>
          ) : (
            <div className="kb-list">
              {articles.map(article => (
                <div key={article._id} className="kb-item">
                  <div className="kb-item-main">
                    <div className="kb-item-head">
                      <h3 className="kb-item-title">{article.title}</h3>
                      <span className={`kb-badge kb-badge-${article.category}`}>
                        {categoryLabel(article.category)}
                      </span>
                      {!article.is_published && (
                        <span className="kb-badge kb-badge-draft">Draft</span>
                      )}
                    </div>
                    <p className="kb-item-preview">
                      {article.content?.slice(0, 220)}
                      {article.content?.length > 220 ? '…' : ''}
                    </p>
                    <div className="kb-item-meta">
                      {article.tags?.length > 0 && (
                        <span className="kb-tags">
                          {article.tags.map(t => (
                            <span key={t} className="kb-tag">{t}</span>
                          ))}
                        </span>
                      )}
                      <span className="kb-updated">
                        Updated {new Date(article.updated_at).toLocaleString()}
                        {article.updated_by ? ` by ${article.updated_by}` : ''}
                      </span>
                    </div>
                  </div>
                  <div className="kb-item-actions">
                    <button onClick={() => startEdit(article)} className="kb-btn-secondary">Edit</button>
                    <button onClick={() => deleteArticle(article)} className="kb-btn-danger">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default KnowledgeBase;
